import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
    // Setup auto-refresh when component mounts
    api.setupAutoRefresh();
  }, []);

  const checkAuth = async () => {
    try {
      // Check if we're in a child session - if so, skip parent auth check
      const childUser = localStorage.getItem('childUser');
      const childToken = localStorage.getItem('childToken');
      
      if (childUser && childToken) {
        // This is a child session, don't try to authenticate as parent
        setLoading(false);
        return;
      }
      
      // Otherwise check for parent auth
      const token = localStorage.getItem('access_token');
      if (token) {
        const userData = await api.getCurrentUser();
        setUser(userData);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('access_token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const data = await api.login(email, password);
    setUser(data.user);
    return data;
  };

  const register = async (email, name, password) => {
    const data = await api.register(email, name, password);
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    await api.logout();
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
